// นักล่าและเหยื่อที่เป็นชนิดจริงแถวที่เราอยู่ (docs/PLAN.md เฟส 3)
// แหล่งชนิด: ฐานข้อมูล (ชนิดที่ crawler บันทึกว่าพบในช่อง 5° เดียวกัน) แล้วเสริมด้วยรายชื่อที่คัดไว้ใน src/data/fauna.ts
// กรองตามตัวกลาง (บก น้ำจืด ทะเล) พื้นที่ ประเภทพื้นที่ แล้วดูว่าใครกินใครได้จากขนาดและชนิดของเหยื่อ

import { FAUNA, type FaunaEntry, type Medium } from "../data/fauna";
import { GROUPS } from "../data/groups";
import { SIZES, SUBGROUP_IDS_WITH_DIET, canEat, defaultSize, dietOf, preyKinds, sizeAtStage, subgroupDiet } from "../data/food-web";
import type { LifeState, Neighbor, SpeciesInfo, StageDef } from "../engine/types";
import { fittingBiomes } from "./biomes";
import type { GeoTrack } from "./locate";

export const NEIGHBOR_LIMIT = 6;

/** ชนิดจากฐานข้อมูลไม่มีกรอบพื้นที่ เพราะดึงมาตามช่องที่เราอยู่แล้ว */
export type NeighborSource = Omit<FaunaEntry, "areas"> & { areas?: FaunaEntry["areas"] };

const AQUATIC_ARCHETYPES = new Set(["FISH", "CRUSTACEAN"]);
const WATER_STAGES = new Set(["tadpole", "larva", "egg"]);
// แมลงที่ลูกน้ำหรือตัวอ่อนอยู่ในน้ำ (ยุง ริ้นน้ำจืด แมลงปอ)
const AQUATIC_LARVA_FAMILIES = new Set(["Culicidae", "Chironomidae"]);
const AQUATIC_LARVA_STAGES = new Set(["larva", "pupa", "nymph"]);

/** ตอนนี้เราอยู่บนบก ในน้ำจืด หรือในทะเล */
export function mediumOf(species: SpeciesInfo, stage: StageDef, track: Pick<GeoTrack, "onSea">): Medium {
  if (track.onSea) return "sea";
  const waterOnly = species.traits.habitat.every((h) => h === "freshwater" || h === "wetland");
  if (AQUATIC_ARCHETYPES.has(species.archetype) && waterOnly) return "freshwater";
  if (species.archetype === "AMPHIBIAN" && WATER_STAGES.has(stage.id)) return "freshwater";
  const aquaticLarva = AQUATIC_LARVA_FAMILIES.has(species.family ?? "") || species.subgroupId === "insect.dragonfly";
  if (aquaticLarva && AQUATIC_LARVA_STAGES.has(stage.id)) return "freshwater";
  return "land";
}

function inArea(entry: NeighborSource, lat: number, lon: number): boolean {
  return !entry.areas || entry.areas.some(([s, w, n, e]) => lat >= s && lat <= n && lon >= w && lon <= e);
}

function fitsBiome(entry: NeighborSource, medium: Medium, biome: GeoTrack["biome"]): boolean {
  if (medium !== "land" || !entry.habitat || !biome) return true;
  const fits = fittingBiomes(entry.habitat);
  return !fits || (fits as ReadonlySet<string>).has(biome);
}

const kindsOf = (entry: NeighborSource) => [entry.subgroupId.split(".")[0], entry.subgroupId];
const toNeighbor = (e: NeighborSource): Neighbor => ({ sci: e.sci, nameTh: e.nameTh, subgroupId: e.subgroupId, ...(e.note ? { note: e.note } : {}) });

export interface NeighborOptions {
  sources?: readonly NeighborSource[]; // ชนิดจากฐานข้อมูลมาก่อน แล้วตามด้วยรายชื่อที่คัดไว้
  ignoreArea?: boolean; // ตัวจำลองไม่มีพิกัดจริง
  limit?: number;
}

/** นักล่าที่กินเราได้ และเหยื่อที่เรากินได้ ณ ตำแหน่งและช่วงวัยนี้ */
export function neighborsAt(
  species: SpeciesInfo,
  stage: StageDef,
  track: Pick<GeoTrack, "at" | "onSea" | "biome">,
  { sources = FAUNA, ignoreArea = false, limit = NEIGHBOR_LIMIT }: NeighborOptions = {},
): { predators: Neighbor[]; prey: Neighbor[] } {
  const medium = mediumOf(species, stage, track);
  const kinds = preyKinds(species, stage);
  const size = sizeAtStage(species, stage);
  // ไข่ ดักแด้ และลูกอ่อนที่พ่อแม่เลี้ยง ยังไม่ได้ล่าเอง ชนิดที่คัดไว้ใช้อาหารที่ระบุเอง
  const diet = stage.tag === "dormant" || stage.tag === "dependent" ? null : (FAUNA.find((f) => f.sci === species.sci)?.diet ?? dietOf(species));
  const seen = new Set<string>([species.sci]);
  const predators: NeighborSource[] = [];
  const prey: NeighborSource[] = [];
  for (const entry of sources) {
    if (seen.has(entry.sci)) continue;
    if (!entry.media.includes(medium)) continue;
    if (!ignoreArea && !inArea(entry, track.at.lat, track.at.lon)) continue;
    if (!fitsBiome(entry, medium, track.biome)) continue;
    seen.add(entry.sci);
    // นักล่าไม่ค่อยล่านักล่าด้วยกันที่ตัวใหญ่เท่าหรือใหญ่กว่าตัวเอง (เสือดาวไม่ล่าเสือโคร่งโตเต็มวัย)
    const rivals = diet !== null && SIZES.indexOf(size) >= SIZES.indexOf(entry.size);
    if (entry.diet && !rivals && canEat(entry.diet, kinds, size)) predators.push(entry);
    if (diet && canEat(diet, kindsOf(entry), entry.size)) prey.push(entry);
  }
  // นักล่าที่ตัวใหญ่กว่าเราราวหนึ่งชั้นมาก่อน (ยุงเจอตุ๊กแกกับค้างคาวก่อนพังพอน)
  // และตัวที่อยู่ในตัวกลางเดียวกับเราตลอด (ปลาในน้ำ) มาก่อนสัตว์บกที่แวะมาล่าริมน้ำ
  const fit = (e: NeighborSource) => Math.abs(SIZES.indexOf(e.size) - SIZES.indexOf(size) - 1) + (e.media.length === 1 ? 0 : 1);
  const hunters = predators.map((e, i) => ({ e, key: fit(e) * 1000 + i })).sort((a, b) => a.key - b.key).map((x) => x.e);
  // เหยื่อหลักคือสัตว์ที่ไม่ได้เป็นนักล่าเอง นักล่าด้วยกันค่อยตามมาทีหลัง
  const ordered = [...prey.filter((e) => !e.diet), ...prey.filter((e) => e.diet)];
  return { predators: hunters.slice(0, limit).map(toNeighbor), prey: ordered.slice(0, limit).map(toNeighbor) };
}

const ALL_SUBGROUPS = GROUPS.flatMap((g) => g.subgroups.map((s) => s.id));

/**
 * กลุ่มย่อยที่อาจเป็นนักล่าหรือเหยื่อของเรา ใช้เลือกว่าจะดึงชนิดกลุ่มไหนจากฐานข้อมูล
 * นักล่าคิดเผื่อตัวใหญ่ที่สุดของกลุ่ม เพราะขนาดในกลุ่มเดียวกันต่างกันได้มาก (งูเหลือมกับงูเขียว)
 */
export function relevantSubgroups(species: SpeciesInfo, stage: StageDef): { predators: string[]; prey: string[] } {
  const kinds = preyKinds(species, stage);
  const size = sizeAtStage(species, stage);
  const predators = SUBGROUP_IDS_WITH_DIET.filter((sg) => SIZES.some((s) => {
    const diet = subgroupDiet(sg, s);
    return diet !== null && canEat(diet, kinds, size);
  }));
  const diet = stage.tag === "dormant" || stage.tag === "dependent" ? null : dietOf(species);
  const prey = diet ? ALL_SUBGROUPS.filter((sg) => canEat(diet, [sg.split(".")[0], sg], defaultSize(sg))) : [];
  return { predators, prey };
}

/** ใส่นักล่าและเหยื่อแถวนี้ลงในสถานะชีวิต (เรียกหลังย้ายตำแหน่งทุก turn) */
export function withNeighbors(state: LifeState, stage: StageDef, options?: NeighborOptions): LifeState {
  if (!state.geo || !state.alive) return state;
  return { ...state, neighbors: neighborsAt(state.species, stage, state.geo, options) };
}
